import React, { useState, useEffect } from "react";
import { InputSwitch } from "primereact/inputswitch";
import { MultiSelect } from "primereact/multiselect";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Password } from "primereact/password";
import { toast } from "react-toastify";

// api's
import SingleUser from "./SingleUser";

// components
import DynamicDataTable from "../Common/DataListing";
// import SummaryCard from '../Common/SummaryCards';


import {
  getUsers,
  addUser,
  editUser,
  deleteUser,
  editUserPermissions,
  getUserDirectPermissions,
  fetchUserPermissions,
  getUserById,
  changeUserPassword,
} from "../../api/userApi";
import { getRoles } from "../../api/roleApi";
import { TabPanel, TabView } from "primereact/tabview";
import { ProgressSpinner } from "primereact/progressspinner";
import { InputText } from "primereact/inputtext";
import { Accordion, AccordionTab } from "primereact/accordion";
import { getPermissions } from "../../api/permissonApi";
import { hasPermission } from "../../utils/permissions";
import { useAuth } from "../../contexts/AuthContext";
import TeachersListing from "../../../components/Teachers/TeachersListing";

// import './UsersList.css'; // Import CSS file

export const ADD_USER_DRAFT_KEY = "sms:addUserDraft";

const UsersList = ({ initialTab }) => {
  const [usersData, setUsersData] = useState([]); //to be used for DataListing
  const [activeTab, setActiveTab] = useState(initialTab === "Teachers" ? 1 : 0);
  const [lockedRole, setLockedRole] = useState(null); // role preset & locked when adding from a role tab
  const [roles, setRoles] = useState([]);
  const [groupedUsers, setGroupedUsers] = useState({}); // For storing grouped users
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [permissionGroups, setPermissionGroups] = useState([]);
  const [userData, setUserData] = useState({
    name: "",
    email: "",
    password: "",
    roleId: "",
    isActive: false,
  }); //to be used for SingleUser (Add/Edit)
  const [userPermissions, setUserPermissions] = useState({});

  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isDirectPermissionsModalVisible, setIsDirectPermissionsModalVisible] =
    useState(false);
  const [isTherapistServicesModalVisible, setIsTherapistServicesModalVisible] =
    useState(false);

  // Search
  const [searchQuery, setSearchQuery] = useState("");

  // Reset password
  const [isResetPasswordVisible, setIsResetPasswordVisible] = useState(false);
  const [resetPasswordTarget, setResetPasswordTarget] = useState(null); // { id, name }
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const { user } = useAuth();
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const rolesData = await getRoles();
        setRoles(rolesData);
      } catch (error) {
        console.error("Error fetching roles:", error);
      }
    };
    fetchRoles();
  }, []);
  useEffect(() => {
    const fetchUserDirectPermissions = async () => {
      try {
        // console.log(selectedUserId);
        const userPermissions = await getUserDirectPermissions(selectedUserId);
        setUserPermissions((prevState) => ({
          ...prevState,
          permissions: userPermissions,
        }));
        // console.log(userPermissions);
      } catch (error) {
        console.error("Error fetching role permissions", error);
      }
    };
    isDirectPermissionsModalVisible && fetchUserDirectPermissions();
    // fetchUserDirectPermissions();
  }, [selectedUserId, isDirectPermissionsModalVisible]);
  useEffect(() => {
    const getPermissions = async () => {
      setLoading(true);
      try {
        const userPermissions = await fetchUserPermissions(user.id);
        // console.log("userPermissions", userPermissions);
        setPermissions(userPermissions);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching permissions:", error);
        setLoading(false);
      }
    };
    getPermissions();
  }, []);
  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const data = await getPermissions();
        setPermissionGroups(data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching permissions", error);
      }
    };

    fetchPermissions();
  }, []);

  const renderIsActiveSwitch = (rowData) => {
    return (
      <InputSwitch
        checked={rowData.isActive}
        onChange={(e) => handleInlineEdit(rowData.id, { isActive: e.value })}
      />
    );
  };
  const renderEmailVerifiedSwitch = (rowData) => {
    return (
      <InputSwitch
        checked={rowData.emailVerified}
        onChange={(e) =>
          handleInlineEdit(rowData.id, { emailVerified: e.value })
        }
      />
    );
  };

  const renderUserName = (rowData) => {
    const isAdmin = rowData.role?.name === "Admin";

    return (
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        {rowData.name}
      </div>
    );
  };

  const renderUserRole = (rowData) => {
    const roleName = rowData.role?.name;

    // Special styling for Admin role
    if (roleName === "Admin") {
      return <span>{roleName}</span>;
    }

    // Default styling for other roles
    return (
      <span
        className=""
        style={{
          background: "#f8f9fa",
          color: "#495057",
          // padding: '4px 8px',
          // borderRadius: '12px',
          // fontSize: '0.9rem'
        }}
      >
        {roleName}
      </span>
    );
  };

  const [columns, setColumns] = useState([
    { field: "name", header: "Name", body: renderUserName },
    { field: "email", header: "Email" },
    { field: "isActive", header: "Is Active", body: renderIsActiveSwitch },
    {
      field: "emailVerified",
      header: "Email Verified?",
      body: renderEmailVerifiedSwitch,
    },
    { header: "Role", body: renderUserRole, nofilter: true },
  ]);
  const filteredRoleColumns = [
    { field: "name", header: "Name", body: renderUserName },
    { field: "email", header: "Email" },
    { field: "isActive", header: "Is Active", body: renderIsActiveSwitch },
    {
      field: "emailVerified",
      header: "Email Verified?",
      body: renderEmailVerifiedSwitch,
    },
  ];
  useEffect(() => {
    fetchUsersData();
  }, []);

  const fetchUsersData = async () => {
    setLoading(true);
    try {
      const data = await getUsers();

      // Sort users to show Admin users first, then others
      const sortedData = data.sort((a, b) => {
        const roleA = a.role?.name || "No Role";
        const roleB = b.role?.name || "No Role";

        // Admin users come first
        if (roleA === "Admin" && roleB !== "Admin") return -1;
        if (roleA !== "Admin" && roleB === "Admin") return 1;

        // If both are Admin or both are not Admin, sort by name
        return a.name.localeCompare(b.name);
      });

      // Group users by role name (Admin will be first due to sorting)
      const grouped = sortedData.reduce((acc, user) => {
        const roleName = user.role?.name || "No Role";
        if (!acc[roleName]) {
          acc[roleName] = [];
        }
        acc[roleName].push(user);
        return acc;
      }, {});

      setUsersData(sortedData); // Store the sorted users data
      setGroupedUsers(grouped); // Group users by role name with Admin first
      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  const responseAlert = (response, autoClose) => {
    if (response.success) {
      toast.success(response.msg, { autoClose: autoClose });
    } else {
      toast.error(response.msg, { autoClose: autoClose });
    }
  };

  const handleInlineEdit = async (userId, userData) => {
    setLoading(true);
    try {
      await editUser(userId, userData);
      await fetchUsersData();
      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };
  const handleCreate = (defaultRoleName) => {
    // Guard: DataListing passes the click event as the first arg
    const roleName = typeof defaultRoleName === "string" ? defaultRoleName : null;
    const presetRoleId = roleName
      ? roles.find((r) => r.name === roleName)?.id ?? ""
      : "";
    setIsEditing(false);
    setLockedRole(roleName);
    let base = {
      name: "",
      email: "",
      roleId: presetRoleId,
      password: "",
      isActive: true,
    };
    try {
      const saved = localStorage.getItem(ADD_USER_DRAFT_KEY);
      if (saved) {
        base = { ...base, ...JSON.parse(saved) };
        if (roleName) base.roleId = presetRoleId; // locked tab wins over draft
        toast.info("Draft restored", { autoClose: 2000 });
      }
    } catch (e) {
      /* corrupt draft — ignore */
    }
    setUserData(base);
    setIsModalVisible(true);
  };

  const handleEdit = async (rowData) => {
    try {
      setLoading(true);
      const fullUserData = await getUserById(rowData.id);

      // Ensure nested teacher objects are initialized if they don't exist to prevent errors
      if (fullUserData.role?.name === 'Teacher') {
        if (!fullUserData.teacher) fullUserData.teacher = {};
        if (!fullUserData.teacherQualifications) fullUserData.teacherQualifications = [];
        if (!fullUserData.teacherWorkHistories) fullUserData.teacherWorkHistories = [];
        if (!fullUserData.teacherCertifications) fullUserData.teacherCertifications = [];
      }

      setIsEditing(true);
      setUserData(fullUserData);
      setSelectedUserId(rowData.id);
      setIsModalVisible(true);
    } catch (e) {
      toast.error("Failed to load user data for editing.");
    } finally {
      setLoading(false);
    }
  };
  // const handleSubmit = async () => {
  //   setLoading(true);
  //   let response = {};
  //   try {
  //     if (isEditing) {
  //       response = await editUser(userData.id, userData); // Update the user
  //     } else {
  //       const selectedRole = roles?.find(role => role.id === userData.roleId);
  //       if (selectedRole?.name === 'Student') {
  //         response = await addUserWithStudent(userData); // Create user with student data
  //       } else {
  //         response = await addUser(userData); // Create a regular user
  //       }
  //       // response = await addUser(userData); // Create a new user
  //     }
  //     await fetchUsersData(); // Refresh the data in the table
  //     setIsModalVisible(false);
  //     responseAlert(response, 300);
  //   } catch (error) {
  //     console.error(error);
  //   } finally {
  //     setLoading(false); // Ensure loading is reset even if an error occurs
  //   }
  // };
  const handleSubmit = async () => {
    setLoading(true);
    let response = {};
    try {
      if (isEditing) {
        response = await editUser(userData.id, userData); // Update the user (includes student data)
      } else {
        response = await addUser(userData); // Create user (includes student data if role is Student)
        if (response?.success !== false) {
          try { localStorage.removeItem(ADD_USER_DRAFT_KEY); } catch (e) { /* ignore */ }
        }
      }
      await fetchUsersData(); // Refresh the data in the table
      setIsModalVisible(false);
      responseAlert(response, 300);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false); // Ensure loading is reset even if an error occurs
    }
  };

  const handleDelete = async (userId) => {
    await deleteUser(userId);
    fetchUsersData();
  };

  const handleDirectPermissions = async (rowData) => {
    setUserData(rowData);
    setSelectedUserId(rowData.id);
    setIsDirectPermissionsModalVisible(true);
  };
  const handleDirectPermissionsSubmit = async () => {
    setLoading(true);
    let response = {};
    // console.log(userPermissions);

    try {
      response = await editUserPermissions(userData.id, userPermissions); // Update the user
      responseAlert(response, 300);
      setIsDirectPermissionsModalVisible(false);
      await fetchUsersData();
    } catch (error) {
      console.error(error);
      responseAlert(response, 300);
    } finally {
      setLoading(false); // Ensure loading is reset even if an error occurs
    }
  };
  // --- Search filtering ---
  const filterUsers = (users) => {
    if (!searchQuery.trim()) return users;
    const q = searchQuery.toLowerCase();
    return users.filter(
      (u) =>
        u.name?.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q) ||
        u.role?.name?.toLowerCase().includes(q)
    );
  };
  const filteredUsersData = filterUsers(usersData);
  const filteredGroupedUsers = Object.fromEntries(
    Object.entries(groupedUsers)
      .map(([role, users]) => [role, filterUsers(users)])
      .filter(([, users]) => users.length > 0)
  );

  // --- Reset password ---
  const handleResetPassword = (rowData) => {
    setResetPasswordTarget({ id: rowData.id, name: rowData.name });
    setNewPassword("");
    setConfirmPassword("");
    setIsResetPasswordVisible(true);
  };

  const handleResetPasswordSubmit = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      const result = await changeUserPassword(resetPasswordTarget.id, newPassword);
      if (result.success) {
        toast.success("Password reset successfully");
        setIsResetPasswordVisible(false);
      } else {
        toast.error(result.msg || "Failed to reset password");
      }
    } catch {
      toast.error("Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  const extraActions = [
    hasPermission(permissions, "User Permission", "read") && {
      label: "Add/Update Direct Permissions",
      className: "btn-outline-primary",
      handler: handleDirectPermissions,
    },
    hasPermission(permissions, "User", "update") && {
      label: "Reset Password",
      icon: "pi pi-lock",
      className: "p-button-warning p-button-sm",
      handler: handleResetPassword,
    },
  ].filter(Boolean);
  const handlePermissionChange = (groupName, selectedPermissions) => {
    // console.log(groupName, selectedPermissions);

    setUserPermissions((prevState) => ({
      ...prevState,
      permissions: {
        ...prevState.permissions,
        [groupName]: selectedPermissions,
      },
    }));
    // setUserPermissions((prevState) => ({
    //   ...prevState,
    //   [groupName]: selectedPermissions,
    // }));
    // console.log(userPermissions);

    // console.log("updated role 2", role);
  };
  return (
    <div>
      {/* <div className="summary-cards-container">
                <SummaryCard title="Total Users" value={usersData.length} />
                <SummaryCard title="Admins" value={usersData.filter(user => user.role === 'Admin').length} />
                <SummaryCard title="Therapists" value={usersData.filter(user => user.role === 'Therapist').length} />
                <SummaryCard title="Parents" value={usersData.filter(user => user.role === 'Parent').length} />
            </div> */}
      {/* Global search bar */}
      <div className="flex align-items-center gap-2 mb-3">
        <span className="p-input-icon-left" style={{ width: "100%", maxWidth: 380 }}>
          <i className="pi pi-search" />
          <InputText
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name, email or role…"
            style={{ width: "100%" }}
          />
        </span>
        {searchQuery && (
          <Button
            icon="pi pi-times"
            className="p-button-text p-button-sm"
            onClick={() => setSearchQuery("")}
            tooltip="Clear search"
          />
        )}
      </div>

      <TabView activeIndex={activeTab} onTabChange={(e) => setActiveTab(e.index)}>
        <TabPanel header="All Users">
          <DynamicDataTable
            data={filteredUsersData}
            subject={"User"}
            columns={columns}
            onCreate={handleCreate}
            onEdit={handleEdit}
            onDelete={handleDelete}
            extraActions={extraActions}
          />
        </TabPanel>
        <TabPanel header="Teachers">
            <TeachersListing
              onEdit={handleEdit}
              onCreate={() => handleCreate("Teacher")}
              onDelete={handleDelete}
              extraActions={extraActions}
              permissions={permissions}
            />
        </TabPanel>
        {Object.entries(filteredGroupedUsers)
          .filter(([roleName]) => roleName !== 'Teacher')
          .map(([roleName, users]) => (
            <TabPanel header={roleName} key={roleName}>
              <DynamicDataTable
                data={users}
                subject={"User"}
                columns={filteredRoleColumns}
                onCreate={() => handleCreate(roleName)}
                onEdit={handleEdit}
                onDelete={handleDelete}
                extraActions={extraActions}
              />
            </TabPanel>
          ))}
      </TabView>
      <Dialog
        header={isEditing ? <label className="text-center text-blue-400 underline text-4xl">Edit User</label> : <h4 className="text-center text-blue-400 underline text-4xl">{`Add New ${lockedRole || "User"}`}</h4>}
        visible={isModalVisible}
        style={{
          width: (() => { const rn = roles.find(r => r.id === userData.roleId)?.name; return (rn === 'Teacher' || rn === 'Student') ? '85vw' : '50vw'; })(),
          minWidth: "520px"
        }}
        onHide={() => setIsModalVisible(false)}
      >
        
        {/* { <hr /> } */}

        <SingleUser
          isChangingPassword={isChangingPassword}
          setIsChangingPassword={setIsChangingPassword}
          user={userData}
          setUser={setUserData}
          onSubmit={handleSubmit}
          isEdit={isEditing}
          lockedRole={isEditing ? null : lockedRole}
          loading={loading}
          setLoading={setLoading}
        />
      </Dialog>

      <Dialog
        header="Direct User Permissions"
        visible={isDirectPermissionsModalVisible}
        style={{ width: "50vw" }}
        onHide={() => setIsDirectPermissionsModalVisible(false)}
      >
        <div className="form-group">
          <div className="p-fluid">
            <div className="p-field">
              <label htmlFor="name">User Name</label>
              <InputText id="name" name="name" value={userData.name} disabled />
            </div>
            <label className="mb-2">User Permissions</label>
            <Accordion multiple>
              {Object.entries(permissionGroups).map(
                ([moduleName, modulePermissions]) => (
                  <AccordionTab key={moduleName} header={moduleName}>
                    <div className="p-field">
                      <MultiSelect
                        id={moduleName}
                        value={userPermissions.permissions?.[moduleName] || []}
                        options={modulePermissions.map((perm) => ({
                          label: `${perm.action} ${perm.subject}`,
                          value: perm.id,
                        }))}
                        onChange={(e) =>
                          handlePermissionChange(moduleName, e.value)
                        }
                        optionLabel="label"
                        optionValue="value"
                        // placeholder={`Select ${moduleName} Permissions`}
                        display="chip"
                        style={{ width: "100%" }}
                      />
                    </div>
                  </AccordionTab>
                )
              )}
            </Accordion>
            <Button
              label="Change Permissions"
              className="btn-primary mt-2"
              onClick={handleDirectPermissionsSubmit}
            />
          </div>
        </div>
      </Dialog>
      {/* Reset Password Dialog */}
      <Dialog
        header={`Reset Password — ${resetPasswordTarget?.name || ""}`}
        visible={isResetPasswordVisible}
        style={{ width: "380px" }}
        onHide={() => setIsResetPasswordVisible(false)}
      >
        <div className="p-fluid flex flex-column gap-3 mt-2">
          <div className="p-field">
            <label htmlFor="rp-new">New Password</label>
            <Password
              id="rp-new"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              toggleMask
              feedback={false}
              style={{ display: "flex" }}
              inputStyle={{ width: "100%" }}
              placeholder="Min. 6 characters"
            />
          </div>
          <div className="p-field">
            <label htmlFor="rp-confirm">Confirm Password</label>
            <Password
              id="rp-confirm"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              toggleMask
              feedback={false}
              style={{ display: "flex" }}
              inputStyle={{ width: "100%" }}
              placeholder="Re-enter password"
            />
          </div>
          <Button
            label="Reset Password"
            icon="pi pi-lock"
            className="btn-primary mt-1"
            onClick={handleResetPasswordSubmit}
            loading={loading}
          />
        </div>
      </Dialog>

      {loading && (
        <div style={spinnerContainerStyle}>
          <ProgressSpinner />
        </div>
      )}
    </div>
  );
};
const spinnerContainerStyle = {
  position: "fixed",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  zIndex: 1000,
};
export default UsersList;
